import { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button, FormField, FormLabel, FormInput, FormSelect } from '../ui';
import { messageTemplateService, sessionService, assistantService } from '../../services';
import type { CampaignFilters as Filters } from '../../types/campaign';
import type { MessageTemplate } from '../../types/messageTemplate';
import type { WhatsAppSession } from '../../types/api';
import type { Assistant } from '../../types/assistant';

interface CampaignFiltersProps {
  filters: Filters;
  onFilterChange: (filters: Filters) => void;
  onClearFilters: () => void;
}

function CampaignFilters({ filters, onFilterChange, onClearFilters }: CampaignFiltersProps) {
  const { data: userTemplates } = useQuery({
    queryKey: ['user-templates'],
    queryFn: () => messageTemplateService.getUserTemplates(),
  });

  const { data: sessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => sessionService.getSessions(),
  });

  const { data: assistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const newFilters: Filters = {
      search: formData.get('search')?.toString() || undefined,
      status: (formData.get('status')?.toString() as Filters['status']) || undefined,
      template_id: formData.get('template_id')?.toString()
        ? parseInt(formData.get('template_id') as string)
        : undefined,
      whatsapp_id: formData.get('whatsapp_id')?.toString()
        ? parseInt(formData.get('whatsapp_id') as string)
        : undefined,
      assistant_id: formData.get('assistant_id')?.toString()
        ? parseInt(formData.get('assistant_id') as string)
        : undefined,
      start_date_from: formData.get('start_date_from')?.toString() || undefined,
      start_date_to: formData.get('start_date_to')?.toString() || undefined,
      end_date_from: formData.get('end_date_from')?.toString() || undefined,
      end_date_to: formData.get('end_date_to')?.toString() || undefined,
      active_only: formData.get('active_only') === 'on',
    };

    onFilterChange(newFilters);
  };

  const hasActiveFilters = Object.values(filters).some(
    value => value !== undefined && value !== false
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow space-y-4"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FormField>
          <FormLabel htmlFor="search">Buscar</FormLabel>
          <FormInput
            id="search"
            name="search"
            type="text"
            placeholder="Nome ou descrição"
            defaultValue={filters.search}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="status">Status</FormLabel>
          <FormSelect id="status" name="status" defaultValue={filters.status || ''}>
            <option value="">Todos</option>
            <option value="active">Ativa</option>
            <option value="paused">Pausada</option>
            <option value="completed">Concluída</option>
            <option value="cancelled">Cancelada</option>
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="template_id">Template</FormLabel>
          <FormSelect id="template_id" name="template_id" defaultValue={filters.template_id || ''}>
            <option value="">Todos</option>
            {(userTemplates || []).map((template: MessageTemplate) => (
              <option key={template.id} value={template.id}>
                {template.name || template.category_description}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="whatsapp_id">Conexão</FormLabel>
          <FormSelect id="whatsapp_id" name="whatsapp_id" defaultValue={filters.whatsapp_id || ''}>
            <option value="">Todas</option>
            {(sessions || []).map((session: WhatsAppSession) => (
              <option key={session.id} value={session.id}>
                {session.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="assistant_id">Assistente</FormLabel>
          <FormSelect
            id="assistant_id"
            name="assistant_id"
            defaultValue={filters.assistant_id || ''}
          >
            <option value="">Todos</option>
            {(assistants || []).map((assistant: Assistant) => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="start_date_from">Início a partir de</FormLabel>
          <FormInput
            id="start_date_from"
            name="start_date_from"
            type="date"
            defaultValue={filters.start_date_from}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="start_date_to">Início até</FormLabel>
          <FormInput
            id="start_date_to"
            name="start_date_to"
            type="date"
            defaultValue={filters.start_date_to}
          />
        </FormField>

        <FormField>
          <div className="flex items-center h-full pt-6">
            <input
              type="checkbox"
              id="active_only"
              name="active_only"
              defaultChecked={filters.active_only}
              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
            <FormLabel htmlFor="active_only" className="ml-2 mb-0">
              Apenas ativas
            </FormLabel>
          </div>
        </FormField>
      </div>

      <div className="flex gap-2">
        <Button type="submit">Filtrar</Button>
        {hasActiveFilters && (
          <Button type="button" variant="secondary" onClick={onClearFilters}>
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>
    </form>
  );
}

export default CampaignFilters;
